import * as THREE from 'three';
import {
  CHARACTER_ENGINE,
  MESSAGE_FROM,
  MESSAGE_TYPES,
  NUDGYT_AGENT_SIMULATION_TYPES
} from '../constants/simulationConstants';
import { normalizeStructuredUserEmotion } from './userEmotion';

export function lerpMorphTarget({ targetBlendShape, value, speed, scene }) {
  if (scene) {
    scene.traverse((child) => {
      if (child.isSkinnedMesh && child.morphTargetDictionary) {
        const index = child.morphTargetDictionary[targetBlendShape];
        if (
          index === undefined ||
          child.morphTargetInfluences[index] === undefined
        )
          return;

        child.morphTargetInfluences[index] = THREE.MathUtils.lerp(
          child.morphTargetInfluences[index],
          value,
          speed
        );
      }
    });
  }
}

export function combineUint8Chunks(chunks) {
  // Guard against invalid input
  if (!Array.isArray(chunks) || chunks.length === 0) {
    return new Uint8Array(0);
  }

  // Filter out any undefined, null, or invalid chunks
  const validChunks = chunks.filter(
    (chunk) => chunk != null && chunk.length != null && chunk.length > 0
  );

  if (validChunks.length === 0) {
    return new Uint8Array(0);
  }

  const totalLength = validChunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const combined = new Uint8Array(totalLength);
  let offset = 0;
  for (const chunk of validChunks) {
    combined.set(chunk, offset);
    offset += chunk.length;
  }
  return combined;
}

export function stripWavHeader(chunk) {
  return chunk.slice(44); // standard PCM WAV header is 44 bytes
}

function fixWavHeaderSize(buffer) {
  const view = new DataView(buffer.buffer);
  // Fix the overall chunk size at offset 4 (little-endian)
  view.setUint32(4, buffer.length - 8, true);
  // Fix the data chunk size at offset 40
  view.setUint32(40, buffer.length - 44, true);
  return buffer;
}

export function assembleWavChunks(chunks) {
  // Guard against invalid input
  if (!Array.isArray(chunks) || chunks.length === 0) {
    return new Blob([], { type: 'audio/wav' });
  }

  // Filter out invalid chunks before processing
  const validChunks = chunks.filter(
    (c) => c != null && c.length != null && c.length > 0
  );

  if (validChunks.length === 0) {
    return new Blob([], { type: 'audio/wav' });
  }

  const [first, ...rest] = validChunks;

  // Guard against undefined first chunk
  if (!first || first.length == null) {
    return new Blob([], { type: 'audio/wav' });
  }

  const stripped = rest
    .filter((c) => c != null && c.length != null && c.length > 46)
    .map(stripWavHeader);
  const allChunks = [first, ...stripped];
  const combined = combineUint8Chunks(allChunks);

  // Guard against empty combined result
  if (combined.length === 0) {
    return new Blob([], { type: 'audio/wav' });
  }

  const fixed = fixWavHeaderSize(combined);
  return new Blob([fixed], { type: 'audio/wav' });
}

export function sanitizeMessage(message) {
  return message
    .replace(/<\/?response>/gi, '') // Remove <response> and </response> tags (case-insensitive)
    .replace(/\bundefined\b/g, '') // Remove undefined
    .replace(/\\(?![ntr"'\\])/g, '') // Remove slashes
    .replace(/\\n/g, '') // Remove any remaining \n escape sequences
    .trim(); // Remove whitespace
}

export function joinWithAnd(items) {
  if (!Array.isArray(items) || items.length === 0) return '';
  const parts = items.map((item) => String(item));
  if (parts.length === 1) return parts[0];
  const head = parts.slice(0, -1).join(', ');
  const tail = parts[parts.length - 1];
  return `${head} and ${tail}`;
}

export function isValidAudioUrl(audioData) {
  if (!audioData) return null;

  try {
    const url = new URL(audioData);
    const isValidProtocol =
      url.protocol === 'http:' || url.protocol === 'https:';

    return isValidProtocol;
  } catch {
    return false;
  }
}

export function postMessage({ type, payload, error }) {
  const message = {
    type,
    ...(payload && { payload }),
    ...(error && { error })
  };
  const messageFormat = JSON.stringify(message);
  window.parent.postMessage(messageFormat, '*');
}


export function postMessageUserText({ text, userEmotion }) {
  const trimmed = typeof text === 'string' ? text.trim() : '';
  if (!trimmed) return;

  const structuredUserEmotion = normalizeStructuredUserEmotion(userEmotion);

  postMessage({
    type: MESSAGE_TYPES.CHAT,
    payload: {
      from: MESSAGE_FROM.USER,
      message: trimmed,
      ...(structuredUserEmotion && {
        userEmotionCore: structuredUserEmotion
      })
    }
  });
}

// Converts base64 audio string to data URL
export function toDataUrl(b64) {
  if (b64.startsWith('data:')) {
    return b64;
  }

  // Check if it's a WAV file by looking for RIFF header in base64
  // "UklGRi" is "RIFF" in base64 (first 4 bytes of WAV file)
  if (b64.startsWith('UklGRi')) {
    return 'data:audio/wav;base64,' + b64;
  }

  // Default to MPEG for backward compatibility
  return 'data:audio/mpeg;base64,' + b64;
}

export function getSelectedPersonalityId(characterIds, selectedCharacterId) {
  const defaultPersonalityId = characterIds[0]?.split('_')[1];
  const personalityId =
    selectedCharacterId?.split('_')[1] ?? defaultPersonalityId;
  return personalityId;
}

/**
 * Helper: Convert Blob to base64 string
 */
export function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      // Remove data URL prefix, keep only base64
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Assembles base64 WAV chunks into a single base64 WAV file
 * Converts base64 strings to Uint8Array, assembles them properly,
 * then converts the final blob back to base64
 */
export async function assembleBase64WavChunks(base64Chunks) {
  if (!Array.isArray(base64Chunks) || base64Chunks.length === 0) {
    throw new Error('Invalid base64 chunks array');
  }

  // Convert base64 strings to Uint8Array
  const uint8Chunks = base64Chunks.map((base64) => {
    // Convert base64 to binary string, then to Uint8Array
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  });

  // Use existing assembleWavChunks to properly combine the chunks
  const audioBlob = assembleWavChunks(uint8Chunks);

  // Convert blob back to base64
  const base64Audio = await blobToBase64(audioBlob);
  return base64Audio;
}

/**
 * Checks if a string contains the exact phrase:
 * "It seems like your message didn't come through."
 */
export function containsMessageNotSent(text) {
  if (!text) return false;

  const sentenceEnders = /[.!?]/g; // split sentences by ., !, ?
  const sentences = text
    .split(sentenceEnders)
    .map((s) => s.trim())
    .filter(Boolean);

  const prefixes = ['It seems like', 'It looks like'];
  const keywords = ["didn't", 'empty', "haven't", 'not', 'lost'];

  for (const sentence of sentences) {
    // Step 1: check if sentence starts with a target prefix
    if (prefixes.some((prefix) => sentence.startsWith(prefix))) {
      // Step 2: check if any keyword exists in this sentence
      if (
        keywords.some((keyword) =>
          sentence.toLowerCase().includes(keyword.toLowerCase())
        )
      ) {
        return true;
      }
    }
  }

  return false;
}

// Create narrative template keys map for Convai Narrative Design
// Use to inject past conversation context into Convai current conversation
export function createNarrativeTemplateKeysMap(previousLevelContext) {
  const narrativeTemplateKeysMap = new Map();
  if (previousLevelContext && typeof previousLevelContext === 'string') {
    narrativeTemplateKeysMap.set('PreviousLevelContext', previousLevelContext);
  }
  return narrativeTemplateKeysMap;
}

/**
 * Get the dominant emotion from expressions
 * @param {Object} expressions - Face API expressions object
 * @param {number} threshold - Minimum value for non-neutral emotion to override neutral (0-1)
 * @returns {Object} { emotion: string, value: number }
 */
export function getDominantEmotion(expressions, threshold) {
  let highestNonNeutral = ['none', 0];
  let highestOverall = ['none', 0];

  for (const [emotion, value] of Object.entries(expressions)) {
    // Track highest overall
    if (value > highestOverall[1]) {
      highestOverall = [emotion, value];
    }

    // Track highest non-neutral
    if (emotion !== 'neutral' && value > highestNonNeutral[1]) {
      highestNonNeutral = [emotion, value];
    }
  }

  // Decide dominant
  const [dominantEmotion, dominantValue] =
    highestNonNeutral[1] >= threshold ? highestNonNeutral : highestOverall;

  return { emotion: dominantEmotion, value: dominantValue };
}

export function isIOS() {
  const ua = navigator.userAgent || '';
  if (/iP(hone|ad|od)/.test(ua)) return true;
  if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    return !/Chrome|Firefox/.test(ua);
  if (/^((?!chrome|android).)*safari/i.test(navigator.userAgent)) return true;
  return false;
}

export function isNudgytAgentSimulationType(simulationType) {
  return NUDGYT_AGENT_SIMULATION_TYPES.includes(simulationType);
}

export function isCoreCharacterEngine(characterEngineType) {
  return characterEngineType === CHARACTER_ENGINE.NUDGYT_CORE;
}

/** Basic simulation whose character converses through Nudgyt Agent (engine Core). */
export function isNudgytAgentCoreSession({ characterEngineType }) {
  return isCoreCharacterEngine(characterEngineType);
}

/**
 * Whether the session runs on the Nudgyt Agent client rather than Convai.
 *
 * True for coaching simulation types, and for Basic + Nudgyt Core engine.
 */
export function isNudgytAgentSession({ simulationType, characterEngineType }) {
  return (
    isNudgytAgentSimulationType(simulationType) ||
    isNudgytAgentCoreSession({ characterEngineType })
  );
}

/** True for Nudgyt Agent clients (Core + Coaching) that use viseme15 + LLM emotion payloads. */
export function isNudgytAgentClient(client) {
  return !!client?.isNudgytAgentClient;
}
